import * as d3 from 'd3';

class MemoryUsageMonitor {
    constructor() {
      this.memoryInfo = {};
      this.resourceInfo = {};
      this.chartsInitialized = false;
    }
  
    getMemoryUsage() {
      if (performance.memory) {
        this.memoryInfo = {
          jsHeapSizeLimit: performance.memory.jsHeapSizeLimit,
          totalJSHeapSize: performance.memory.totalJSHeapSize,
          usedJSHeapSize: performance.memory.usedJSHeapSize
        };
      } else {
        this.memoryInfo = { error: "Performance.memory is not available in this browser." };
      }
      return this.memoryInfo;
    }
  
    getResourceUsage() {
      const resources = performance.getEntriesByType('resource');
      this.resourceInfo = {};
  
      resources.forEach(resource => {
        const size = resource.transferSize || resource.decodedBodySize || 0;
        const type = resource.initiatorType || 'unknown';
  
        if (!this.resourceInfo[type]) {
          this.resourceInfo[type] = { count: 0, size: 0 };
        }
  
        this.resourceInfo[type].count++;
        this.resourceInfo[type].size += size;
      });
  
      return this.resourceInfo;
    }
  
    getScriptMemoryUsage() {
      const scripts = document.getElementsByTagName('script');
      let totalSize = 0;
    
      for (let script of scripts) {
        if (script.src) {
          // For external scripts, we'll just use the length of the src attribute as an estimate
          totalSize += script.src.length;
        } else {
          totalSize += new Blob([script.innerHTML]).size;
        }
      }
    
      return { scriptSize: totalSize };
    }
  
    formatBytes(bytes, decimals = 2) {
      if (bytes === 0) return '0 Bytes';
  
      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  
      const i = Math.floor(Math.log(bytes) / Math.log(k));
  
      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    }
  
    getAllMemoryInfo() {
      return {
        overallMemory: this.getMemoryUsage(),
        resourceUsage: this.getResourceUsage(),
        scriptMemoryUsage: this.getScriptMemoryUsage()
      };
    }
  
    visualizeMemoryUsage(containerId) {
      const data = this.getAllMemoryInfo();
      
      if (!this.chartsInitialized) {
        // Create container for charts if it doesn't exist
        const container = d3.select(`#${containerId}`)
          .append('div')
          .attr('class', 'memory-charts-container')
          .style('display', 'flex')
          .style('justify-content', 'space-between')
          .style('align-items', 'center')
          .style('flex-wrap', 'nowrap');
  
        // Create placeholders for charts
        container.append('div').attr('class', 'overall-memory-chart').style('flex', '1 1 50%');
        container.append('div').attr('class', 'resource-usage-chart').style('flex', '1 1 50%');
  
        this.chartsInitialized = true;
      }
  
      // Update or create charts
      this.updateBarChart(data.overallMemory, 'Overall Memory Usage');
      this.updatePieChart(data.resourceUsage, 'Resource Usage');
    }
  
    updateBarChart(data, title) {
      const margin = {top: 30, right: 30, bottom: 70, left: 60};
      const width = 460 - margin.left - margin.right;
      const height = 400 - margin.top - margin.bottom;
  
      const container = d3.select('.overall-memory-chart');
  
      // Check if SVG exists, if not create it
      let svg = container.select('svg');
      if (svg.empty()) {
        svg = container.append('svg')
          .attr('width', width + margin.left + margin.right)
          .attr('height', height + margin.top + margin.bottom)
          .append('g')
          .attr('transform', `translate(${margin.left},${margin.top})`);
  
        // Create scales
        this.xScale = d3.scaleBand()
          .range([0, width])
          .padding(0.2);
  
        this.yScale = d3.scaleLinear()
          .range([height, 0]);
  
        // Create axes
        svg.append('g')
          .attr('class', 'x-axis')
          .attr('transform', `translate(0,${height})`);
  
        svg.append('g')
          .attr('class', 'y-axis');
  
        // Create title
        svg.append('text')
          .attr('class', 'chart-title')
          .attr('x', width / 2)
          .attr('y', 0 - (margin.top / 2))
          .attr('text-anchor', 'middle')
          .style('font-size', '16px');
      }
  
      // Update scales
      this.xScale.domain(Object.keys(data));
      this.yScale.domain([0, d3.max(Object.values(data))]);
  
      // Update axes
      svg.select('.x-axis')
        .call(d3.axisBottom(this.xScale))
        .selectAll('text')
          .attr('transform', 'translate(-10,0)rotate(-45)')
          .style('text-anchor', 'end');
  
      svg.select('.y-axis')
        .call(d3.axisLeft(this.yScale));
  
      // Update bars
      const bars = svg.selectAll('.bar')
        .data(Object.entries(data));
  
      bars.enter()
        .append('rect')
        .attr('class', 'bar')
        .merge(bars)
        .transition()
        .duration(300)
        .attr('x', d => this.xScale(d[0]))
        .attr('y', d => this.yScale(d[1]))
        .attr('width', this.xScale.bandwidth())
        .attr('height', d => height - this.yScale(d[1]))
        .attr('fill', '#69b3a2');
  
      bars.exit().remove();
  
      // Update title
      svg.select('.chart-title').text(title);
    }
  
    updatePieChart(data, title) {
      const width = 450;
      const height = 450;
      const radius = Math.min(width, height) / 2;
  
      const container = d3.select('.resource-usage-chart');
  
      // Check if SVG exists, if not create it
      let svg = container.select('svg');
      if (svg.empty()) {
        svg = container.append('svg')
          .attr('width', width)
          .attr('height', height)
          .append('g')
          .attr('transform', `translate(${width / 2},${height / 2})`);
  
        // Create title
        svg.append('text')
          .attr('class', 'chart-title')
          .attr('x', 0)
          .attr('y', -height / 2 + 20)
          .attr('text-anchor', 'middle')
          .style('font-size', '16px');
  
        // Create tooltip
        container.append('div')
          .attr('class', 'tooltip')
          .style('opacity', 0)
          .style('position', 'absolute')
          .style('background-color', 'white')
          .style('border', 'solid')
          .style('border-width', '1px')
          .style('border-radius', '5px')
          .style('padding', '10px');
      }
  
      const color = d3.scaleOrdinal()
        .domain(Object.keys(data))
        .range(d3.schemeCategory10);
  
      const pie = d3.pie()
        .value(d => d.value.size);
  
      const data_ready = pie(Object.entries(data).map(([key, value]) => ({key, value})));
  
      const arcGenerator = d3.arc()
        .innerRadius(0)
        .outerRadius(radius);
  
      // Update paths
      const paths = svg.selectAll('path')
        .data(data_ready);
  
      paths.enter()
        .append('path')
        .merge(paths)
        .transition()
        .duration(300)
        .attr('d', arcGenerator)
        .attr('fill', d => color(d.data.key))
        .attr('stroke', 'white')
        .style('stroke-width', '2px');
  
      paths.exit().remove();
  
      // Update labels
      const arcLabel = d3.arc().innerRadius(radius * 0.6).outerRadius(radius);
  
      const labels = svg.selectAll('.pie-label')
        .data(data_ready);
  
      labels.enter()
        .append('text')
        .attr('class', 'pie-label')
        .merge(labels)
        .transition()
        .duration(300)
        .attr('transform', d => `translate(${arcLabel.centroid(d)})`)
        .attr('dy', '0.35em')
        .attr('text-anchor', 'middle')
        .style('font-size', '12px')
        .style('fill', 'white')
        .each(function(d) {
          const text = d3.select(this);
          const lines = [
            d.data.key,
            `${(d.data.value.size / 1024).toFixed(2)} KB`
          ];
          text.selectAll('tspan').remove();
          text.selectAll('tspan')
            .data(lines)
            .enter()
            .append('tspan')
            .attr('x', 0)
            .attr('dy', (_, i) => i ? '1.2em' : 0)
            .text(d => d);
        });
  
      labels.exit().remove();
  
      // Add hover effects and tooltip
      const tooltip = container.select('.tooltip');
  
      const totalSize = d3.sum(Object.values(data), d => d.size);
  
      svg.selectAll('path')
        .on('mouseover', (event, d) => {
          const percent = ((d.data.value.size / totalSize) * 100).toFixed(2);
          tooltip.transition()
            .duration(200)
            .style('opacity', .9);
          tooltip.html(`
            <strong>${d.data.key}</strong><br>
            Size: ${this.formatBytes(d.data.value.size)}<br>
            Count: ${d.data.value.count}<br>
            Percentage: ${percent}% of total<br>
            <em>This slice represents ${percent}% of the total resource usage.</em>
          `)
            .style('left', (event.pageX) + 'px')
            .style('top', (event.pageY - 28) + 'px');
        })
        .on('mouseout', () => {
          tooltip.transition()
            .duration(500)
            .style('opacity', 0);
        });
  
      // Update title
      svg.select('.chart-title').text(title);
    }
  }

  export default MemoryUsageMonitor;